/**
 * Liore feature statistics.
 *
 * Publishes named events on Shopify's customer-events bus
 * (`Shopify.analytics.publish`). A custom pixel (Settings → Customer events)
 * forwards them to GA4, and Shopify only runs that pixel for visitors who
 * accepted analytics cookies, so nothing here is sent without consent.
 * Events carry feature facts only (step, platform, product handle, counts),
 * never anything about the visitor.
 *
 * Two ways in:
 * - `window.lioreTrack(name, params)` for scripts (routine builder, add-all,
 *   share, social rail, article cards);
 * - `data-track="event_name"` plus `data-track-<param>` on any link or button,
 *   reported on click by the listener below.
 * Links to the routine builder page are reported as `routine_link_clicked`
 * without any markup; their location comes from where on the page they sit.
 *
 * Imports nothing, and only touches `document` inside `init`, so the helpers
 * can be unit tested under `node --test`.
 */

const MAX_TEXT = 100;

/** Plain, short values only; anything else is dropped. */
export function cleanParams(params = {}) {
  const out = {};
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'string' && value !== '') out[key] = value.slice(0, MAX_TEXT);
    else if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
    else if (typeof value === 'boolean') out[key] = value;
  }
  return out;
}

/** `data-track-link-location="footer"` → `{ link_location: 'footer' }`. */
export function datasetParams(dataset = {}) {
  const out = {};
  for (const [key, value] of Object.entries(dataset)) {
    if (!key.startsWith('track') || key === 'track') continue;
    const name = key
      .slice('track'.length)
      .replace(/^[A-Z]/, (c) => c.toLowerCase())
      .replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
    out[name] = value;
  }
  return out;
}

/** Whether a link's path is the routine builder page in any language. */
export function isRoutinePath(pathname, paths) {
  const clean = (path) => String(path || '').replace(/\/+$/, '');
  return paths.some((path) => path && clean(path) === clean(pathname));
}

/**
 * Where a routine link sits, most specific first.
 * @param {(selector: string) => boolean} within whether the link is inside a match
 */
export function linkLocation(within) {
  const places = [
    ['header-drawer', 'phone_menu'],
    ['header', 'desktop_menu'],
    ['footer, .footer', 'footer'],
    ['.liore-start__routine', 'collections_card'],
    ['.liore-starter__routine', 'home_starter'],
  ];
  return places.find(([selector]) => within(selector))?.[1] ?? 'other';
}

/** Publish one event; a page without Shopify analytics simply skips it. */
export function track(name, params = {}) {
  try {
    globalThis.Shopify?.analytics?.publish?.(name, cleanParams(params));
  } catch {
    // Statistics must never break the page.
  }
}

function init() {
  window.lioreTrack = track;
  const config = document.getElementById('LioreTrack');
  const routinePaths = (config?.dataset.routinePaths || '').split(',').filter(Boolean);

  // Capture phase, so handlers that stop or prevent the click (the social
  // rail opens posts in place) cannot hide it from the statistics.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const marked = target?.closest('[data-track]');
      if (marked instanceof HTMLElement) {
        track(marked.dataset.track || '', datasetParams(marked.dataset));
        return;
      }
      const link = target?.closest('a[href]');
      if (link instanceof HTMLAnchorElement && isRoutinePath(link.pathname, routinePaths)) {
        track('routine_link_clicked', { link_location: linkLocation((selector) => Boolean(link.closest(selector))) });
      }
    },
    { capture: true },
  );
}

if (typeof document !== 'undefined') init();
